/**
 * Hooks para COM-01..05 (Compras y Proveedores)
 */
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import { emitToast } from '@/lib/globalToast'

/** Importar un archivo puede tardar minutos: el timeout por defecto (30 s) corta
 *  el request mientras el servidor sigue escribiendo. */
const IMPORT_TIMEOUT_MS = 300_000

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Supplier {
  id: string
  legal_name: string
  trade_name: string
  tax_id: string
  tax_id_label: string
  tax_id_type: 'RUT' | 'EIN' | 'VAT' | 'OTHER'
  person_type: 'juridica' | 'natural'
  person_type_display: string
  is_international: boolean
  country: string
  default_currency: string
  default_expense_account: string | null
  default_payable_account: string | null
  email_identifiers: string[]
  contact_name: string
  email: string
  phone: string
  address_street: string
  address_commune: string
  address_city: string
  address_region: string
  giro: string
  website: string
  notes: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface SupplierListItem {
  id: string
  legal_name: string
  trade_name: string
  tax_id: string
  person_type: string
  email: string
  phone: string
  contact_name: string
  giro: string
  is_active: boolean
  created_at: string
}

export interface SupplierListResponse {
  suppliers: SupplierListItem[]
  count: number
  page: number
  page_size: number
  pages: number
}

export interface CostCenterAllocation {
  cost_center_id: string
  percentage: string
}

export interface POLine {
  sku: string
  name: string
  quantity: string
  unit_price: string
  subtotal?: string
  is_tax_exempt?: boolean
  cost_center_allocations?: CostCenterAllocation[]
  // Multimoneda (facturas internacionales): el monto en la moneda original; el
  // unit_price/subtotal de arriba quedan en CLP (= original × tasa).
  currency_original?: string
  unit_price_original?: string
  amount_original?: string
  /** Descuento de la línea (CLP). El proveedor puede aplicarlo por ítem: el
   *  monto de la línea es cantidad × precio − descuento. */
  discount?: string
  /** El mismo descuento en la moneda original del documento. */
  discount_original?: string
}

export type PurchaseOrderStatus = 'draft' | 'approved' | 'sent' | 'received' | 'cancelled'

export interface PurchaseOrderInvoiceRef {
  id: string
  invoice_number: string
  status: string
  status_display: string
  total: string
}

export interface PurchaseOrder {
  id: string
  order_number: string
  supplier: string
  supplier_name: string
  status: PurchaseOrderStatus
  status_display: string
  order_date: string
  expected_date?: string | null
  notes: string
  payment_terms: string
  lines: POLine[]
  subtotal: string
  tax_amount: string
  total: string
  currency: string
  cancellation_reason: string
  expense_account: string | null
  expense_account_display: string | null
  approver: string | null
  approver_name: string | null
  requested_approval_at: string | null
  approved_by: string | null
  approved_by_name: string | null
  approved_at: string | null
  invoice: PurchaseOrderInvoiceRef | null
  created_at: string
  updated_at: string
}

export interface PurchaseOrderListItem {
  id: string
  order_number: string
  supplier: string
  supplier_name: string
  supplier_tax_id: string
  status: PurchaseOrderStatus
  status_display: string
  order_date: string
  expected_date?: string | null
  subtotal: string
  tax_amount: string
  total: string
  currency: string
  approver: string | null
  approver_name: string | null
  requested_approval_at: string | null
  has_invoice: boolean
  created_at: string
}

export interface ApproverUser {
  id: string
  email: string
  full_name: string
}

export interface PurchaseOrderListResponse {
  orders: PurchaseOrderListItem[]
  count: number
  page: number
  page_size: number
  pages: number
}

export interface GoodsReceipt {
  id: string
  purchase_order: string
  purchase_order_id: string
  supplier: string
  supplier_name: string
  lines: POLine[]
  notes: string
  received_at: string
}

export interface GoodsReceiptListResponse {
  receipts: GoodsReceipt[]
  count: number
  page: number
  page_size: number
  pages: number
}

export type SupplierInvoiceStatus = 'rcv_pending' | 'rcv_reclamado' | 'registered' | 'approved' | 'paid' | 'cancelled'
export type SupplierInvoiceSource = 'manual' | 'xml_import' | 'rcv' | 'intl_invoice' | 'honorarios'

export type APAdjustmentStatus = 'normal' | 'adjusted' | 'annulled' | 'annulled_after_payment'

export interface APInvoiceReference {
  tipo_doc: string
  folio_ref: string
  fecha_ref: string
  cod_ref: string
  razon_ref: string
}

export interface APInvoiceAdjustment {
  id: string
  type: 'NC' | 'ND'
  document_type_sii: number
  folio: string
  issue_date: string
  amount: string
  reason: string
}

/** Acuse/reclamo SII (Ley 19.983) del DTE recibido vinculado a la factura. */
export interface SiiAcuse {
  /** `null` en una factura que se conoce solo por el RCV (sin XML, #1637). */
  inbound_dte_id: string | null
  /** Sin XML: se responde por RUT, tipo y folio, sin respuesta al proveedor. */
  sin_xml?: boolean
  forma_pago: number | null
  /** `null` sin XML: no se sabe si es a crédito. */
  is_credit: boolean | null
  dte_status: string | null
  can_acuse?: boolean
  accepted: boolean
  rejected: boolean
  sii_event_registered: boolean
  can_accept: boolean
  can_reject: boolean
}

/** Montos especiales del DTE (del XML) para documentos donde MntNeto/MntTotal
 *  vienen en 0 y el monto real va en otro campo (no facturable / período /
 *  exento). Solo trae las claves presentes (≠ 0). Caso típico: Banchile. */
export interface SiiMontos {
  monto_nf?: string
  monto_periodo?: string
  exento?: string
  saldo_anterior?: string
  vlr_pagar?: string
}

export interface SupplierInvoice {
  id: string
  supplier: string
  supplier_name: string
  purchase_order: string | null
  invoice_number: string
  issue_date: string
  due_date?: string | null
  status: SupplierInvoiceStatus
  status_display: string
  lines: POLine[]
  referencias?: APInvoiceReference[]
  subtotal: string
  tax_amount: string
  total: string
  notes: string
  accounting_status?: 'pending' | 'accounted'
  source: SupplierInvoiceSource
  document_type_sii?: number | null
  rcv_estado?: string
  rcv_fecha_recepcion?: string | null
  rcv_fecha_acuse?: string | null
  rcv_fecha_reclamo?: string | null
  rcv_deadline?: string | null
  days_until_deadline?: number | null
  has_xml: boolean
  has_original_pdf?: boolean
  rcv_xml_mismatch?: boolean
  outstanding?: string
  adjustment_status?: APAdjustmentStatus
  adjustments?: APInvoiceAdjustment[]
  is_adjustment_orphan?: boolean
  currency_original?: string | null
  total_original?: string | null
  /** Saldo en la moneda original (solo factura en moneda extranjera; null si no). */
  outstanding_original?: string | null
  sii_acuse?: SiiAcuse | null
  sii_montos?: SiiMontos
  /** Total tributario 0 pero con monto no facturable (deuda real en sii_montos). */
  is_non_billable?: boolean
  /** Ajuste que el proveedor aplica DESPUÉS de emitir, sin nota de crédito
   *  (caso Freshworks). Monto positivo: se resta del total. */
  external_adjustment?: string
  external_adjustment_original?: string
  external_adjustment_note?: string
  /** Monto realmente debido: total − NC + ND − ajuste externo. */
  adjusted_total?: string
  /** Aviso del backend: la factura se guardó (editada, aprobada o cargada desde
   *  el XML) pero el comprobante contable no se pudo crear. */
  accounting_warning?: string
  created_at: string
  updated_at: string
}

export interface SupplierInvoiceListItem {
  id: string
  supplier: string
  supplier_name: string
  invoice_number: string
  issue_date: string
  due_date?: string | null
  status: SupplierInvoiceStatus
  status_display: string
  subtotal: string
  tax_amount: string
  total: string
  accounting_status?: 'pending' | 'accounted'
  payment_status?: '' | 'pending' | 'partial' | 'paid' | 'overdue' | 'paid_historical'
  payment_status_display?: string
  source: SupplierInvoiceSource
  document_type_sii?: number | null
  rcv_estado?: string
  rcv_fecha_recepcion?: string | null
  rcv_deadline?: string | null
  days_until_deadline?: number | null
  has_xml: boolean
  has_original_pdf?: boolean
  rcv_xml_mismatch?: boolean
  outstanding?: string
  adjustment_status?: APAdjustmentStatus
  adjustments?: APInvoiceAdjustment[]
  is_adjustment_orphan?: boolean
  currency_original?: string | null
  total_original?: string | null
  sii_acuse?: SiiAcuse | null
  sii_montos?: SiiMontos
  /** Total tributario 0 pero con monto no facturable (deuda real en sii_montos). */
  is_non_billable?: boolean
  created_at: string
}

export interface SupplierInvoiceListResponse {
  invoices: SupplierInvoiceListItem[]
  count: number
  page: number
  page_size: number
  pages: number
}

/** Monto no facturable "efectivo" del documento (período > no facturable >
 *  exento), o null si no hay. Sirve para mostrar el monto real cuando el total
 *  viene en 0 (corredoras / servicios periódicos). Se devuelve en valor absoluto:
 *  una venta de divisa llega con monto negativo pero se muestra como deuda en
 *  positivo (idem SupplierInvoice.non_billable_amount del backend). */
export function nonBillableAmount(m?: SiiMontos | null): string | null {
  if (!m) return null
  const raw = m.monto_periodo || m.monto_nf || m.exento || null
  if (!raw) return null
  return raw.startsWith('-') ? raw.slice(1) : raw
}

/** Total real a pagar cuando el documento trae un monto no facturable y el total
 *  tributario es > 0 (telecom: VlrPagar = afecto + equipos en cuotas / cargos de
 *  terceros). Devuelve el VlrPagar/MontoPeriodo (abs) solo si supera el total, o
 *  null (idem SupplierInvoice._vlr_pagar_extra + outstanding_amount del backend). */
export function payableTotal(m: SiiMontos | null | undefined, total: string): string | null {
  if (!m) return null
  const raw = m.vlr_pagar || m.monto_periodo
  if (!raw) return null
  const vp = Math.abs(parseFloat(raw))
  const t = parseFloat(total)
  if (!isFinite(vp) || vp === 0 || !isFinite(t) || !(vp > t)) return null
  return String(vp)
}

export type PaymentMethod = 'transfer' | 'check' | 'cash' | 'servipag' | 'other'
export type SupplierPaymentStatus = 'registered' | 'confirmed' | 'cancelled'

export interface SupplierPayment {
  id: string
  supplier: string
  supplier_name: string
  invoice: string | null
  payment_date: string
  amount: string
  payment_method: PaymentMethod
  payment_method_display: string
  reference: string
  bank_account: string | null
  status: SupplierPaymentStatus
  status_display: string
  notes: string
  currency?: string
  exchange_rate?: string | null
  exchange_rate_date?: string | null
  amount_clp?: string
  accounting_status?: 'pending' | 'accounted'
  created_by_name?: string | null
  /** El backend crea el comprobante del pago en borrador cuando la factura tiene
   *  monto no facturable (divisa/servicio periódico) → revisar la cuenta contable. */
  voucher_draft_warning?: boolean
  /** Aviso del backend: el pago quedó confirmado pero sin comprobante contable. */
  accounting_warning?: string
  created_at: string
  updated_at: string
}

export interface SupplierPaymentListItem {
  id: string
  supplier: string
  supplier_name: string
  invoice: string | null
  payment_date: string
  amount: string
  payment_method: PaymentMethod
  payment_method_display: string
  reference: string
  status: SupplierPaymentStatus
  status_display: string
  accounting_status?: 'pending' | 'accounted'
  currency?: string
  exchange_rate?: string | null
  amount_clp?: string
  created_by_name?: string | null
  created_at: string
}

export interface SupplierPaymentListResponse {
  payments: SupplierPaymentListItem[]
  count: number
  page: number
  page_size: number
  pages: number
}

// ─── Dashboard Types ──────────────────────────────────────────────────────────

export interface PurchasesDashboard {
  kpis: {
    invoiced_this_month: string
    pending_payable: string
    paid_this_month: string
    overdue_count: number
  }
  ap_aging: {
    current:  { count: number; total: string }
    '1_30':   { count: number; total: string }
    '31_60':  { count: number; total: string }
    '61_90':  { count: number; total: string }
    over_90:  { count: number; total: string }
  }
  top_suppliers: Array<{
    supplier_id: string
    supplier_name: string
    total: string
  }>
  currency: string
}

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const purchaseKeys = {
  dashboard: () => ['purchases', 'dashboard'] as const,
  suppliers: (params?: object) => ['purchases', 'suppliers', params] as const,
  supplier: (id: string) => ['purchases', 'suppliers', id] as const,
  orders: (params?: object) => ['purchases', 'orders', params] as const,
  order: (id: string) => ['purchases', 'orders', id] as const,
  receipts: (params?: object) => ['purchases', 'receipts', params] as const,
  receipt: (id: string) => ['purchases', 'receipts', id] as const,
  invoices: (params?: object) => ['purchases', 'invoices', params] as const,
  invoice: (id: string) => ['purchases', 'invoices', id] as const,
  invoiceAttachments: (id: string) => ['purchases', 'invoices', id, 'attachments'] as const,
  payments: (params?: object) => ['purchases', 'payments', params] as const,
  payment: (id: string) => ['purchases', 'payments', id] as const,
}

// ─── Dashboard & Upload XML ───────────────────────────────────────────────────

export function usePurchasesDashboard() {
  return useQuery<PurchasesDashboard>({
    queryKey: purchaseKeys.dashboard(),
    queryFn: async () => {
      const res = await apiClient.get('/purchases/dashboard/')
      return res.data
    },
    staleTime: 60_000,
  })
}

/**
 * El backend aprueba la factura o confirma el pago aunque no haya podido crear
 * el comprobante contable, y lo avisa en `accounting_warning` (sin cuentas por
 * defecto, montos en cero o un error). Va en el hook y no en cada pantalla
 * porque hay siete lugares que aprueban, cargan XML o pagan, y bastaba que uno
 * lo olvidara para volver al silencio de antes: «aprobada» y sin asiento.
 */
function avisarSiNoSeContabilizo(data: { accounting_warning?: string } | undefined) {
  if (data?.accounting_warning) emitToast('warning', data.accounting_warning)
}

export function useUploadXMLInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('xml_file', file)
      const res = await apiClient.post('/purchases/invoices/upload-xml/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data as SupplierInvoice
    },
    onSuccess: (data) => {
      avisarSiNoSeContabilizo(data)
      qc.invalidateQueries({ queryKey: ['purchases', 'invoices'] })
    },
  })
}

// ─── Adjuntos de factura de proveedor (PDF original) ─────────────────────────

export interface SupplierInvoiceAttachment {
  id: string
  kind: 'original_pdf' | 'other'
  kind_display: string
  file_name: string
  file_size: number
  content_type: string
  download_url: string
  uploaded_by_name: string | null
  created_at: string
  /** 'voucher' = el PDF vive en el comprobante (facturas intl); read-only desde la factura. */
  source?: 'voucher'
}

export interface SupplierInvoiceAttachmentsResponse {
  count: number
  total_size: number
  max_size: number
  attachments: SupplierInvoiceAttachment[]
}

export function useSupplierInvoiceAttachments(invoiceId: string) {
  return useQuery<SupplierInvoiceAttachmentsResponse>({
    queryKey: purchaseKeys.invoiceAttachments(invoiceId),
    queryFn: async () => {
      const res = await apiClient.get(`/purchases/invoices/${invoiceId}/attachments/`)
      return res.data
    },
    enabled: !!invoiceId,
  })
}

export function useUploadSupplierInvoiceOriginalPdf(invoiceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      form.append('kind', 'original_pdf')
      const res = await apiClient.post(`/purchases/invoices/${invoiceId}/attachments/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data as SupplierInvoiceAttachment
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: purchaseKeys.invoiceAttachments(invoiceId) })
      qc.invalidateQueries({ queryKey: ['purchases', 'invoices'] })
    },
  })
}

export function useDeleteSupplierInvoiceAttachment(invoiceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (attachmentId: string) => {
      await apiClient.delete(`/purchases/invoices/${invoiceId}/attachments/${attachmentId}/`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: purchaseKeys.invoiceAttachments(invoiceId) })
      qc.invalidateQueries({ queryKey: ['purchases', 'invoices'] })
    },
  })
}

// ─── COM-01: Proveedores ──────────────────────────────────────────────────────

export function useSuppliers(params?: {
  search?: string
  active?: string
  page?: number
  page_size?: number
  ordering?: string
}) {
  return useQuery<SupplierListResponse>({
    queryKey: purchaseKeys.suppliers(params),
    queryFn: async () => {
      const res = await apiClient.get('/purchases/suppliers/', { params })
      return res.data
    },
  })
}

/**
 * El detalle completo de un proveedor. El listado trae un subconjunto de campos
 * (sin país, moneda, cuentas ni correos de auto-matching): abrir el modal con
 * esa fila y guardar borraba la configuración internacional (M03-VC-05).
 */
export function useSupplier(id: string | null) {
  return useQuery<Supplier>({
    queryKey: purchaseKeys.supplier(id ?? ''),
    queryFn: async () => (await apiClient.get(`/purchases/suppliers/${id}/`)).data,
    enabled: !!id,
  })
}

export function useCreateSupplier() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<Supplier>) => {
      const res = await apiClient.post('/purchases/suppliers/', data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'suppliers'] })
    },
  })
}

export function useUpdateSupplier(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<Supplier>) => {
      const res = await apiClient.patch(`/purchases/suppliers/${id}/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'suppliers'] })
      qc.invalidateQueries({ queryKey: purchaseKeys.supplier(id) })
    },
  })
}

export function useDeactivateSupplier() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/purchases/suppliers/${id}/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'suppliers'] })
    },
  })
}

export function useRestoreSupplier() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/purchases/suppliers/${id}/restore/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'suppliers'] })
    },
  })
}

// ─── COM-02: Órdenes de Compra ────────────────────────────────────────────────

export function usePurchaseOrders(params?: {
  status?: string
  supplier_id?: string
  search?: string
  page?: number
  page_size?: number
}) {
  return useQuery<PurchaseOrderListResponse>({
    queryKey: purchaseKeys.orders(params),
    queryFn: async () => {
      const res = await apiClient.get('/purchases/orders/', { params })
      return res.data
    },
  })
}

export function usePurchaseOrder(id: string) {
  return useQuery<PurchaseOrder>({
    queryKey: purchaseKeys.order(id),
    queryFn: async () => {
      const res = await apiClient.get(`/purchases/orders/${id}/`)
      return res.data
    },
    enabled: !!id,
  })
}

export interface PurchaseOrderFormData {
  supplier: string
  lines: POLine[]
  currency?: string
  notes?: string
  payment_terms?: string
  expected_date?: string
  expense_account?: string | null
}

export function useCreatePurchaseOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: PurchaseOrderFormData) => {
      const res = await apiClient.post('/purchases/orders/', data)
      return res.data as PurchaseOrder
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'orders'] })
    },
  })
}

/** Descarga el PDF de la OC autenticado vía blob (evita problemas con X-Frame-Options). */
export async function downloadPurchaseOrderPdf(id: string, filename: string) {
  const res = await apiClient.get(`/purchases/orders/${id}/pdf/`, { responseType: 'blob' })
  const blobUrl = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = filename
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
}

export function useUpdatePurchaseOrder(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<PurchaseOrder>) => {
      const res = await apiClient.patch(`/purchases/orders/${id}/`, data)
      return res.data as PurchaseOrder
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'orders'] })
      qc.invalidateQueries({ queryKey: purchaseKeys.order(id) })
    },
  })
}

export function useApprovePurchaseOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/purchases/orders/${id}/approve/`)
      return res.data as PurchaseOrder
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['purchases', 'orders'] })
      // Invalidar también el detalle para que el modal refleje el nuevo estado
      // tras aprobar desde ahí (#47 — aprobar desde móvil vía modal de detalle).
      qc.invalidateQueries({ queryKey: purchaseKeys.order(id) })
    },
  })
}

export function useRequestPurchaseOrderApproval() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, approverId }: { id: string; approverId: string }) => {
      const res = await apiClient.post(`/purchases/orders/${id}/request-approval/`, {
        approver_id: approverId,
      })
      return res.data as PurchaseOrder
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['purchases', 'orders'] })
      qc.invalidateQueries({ queryKey: purchaseKeys.order(vars.id) })
    },
  })
}

export interface ConvertOrderToInvoiceInput {
  id: string
  invoice_number: string
  issue_date: string
  due_date?: string
  expense_account_id?: string
}

export function useConvertOrderToInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: ConvertOrderToInvoiceInput) => {
      const res = await apiClient.post(`/purchases/orders/${data.id}/convert-to-invoice/`, {
        invoice_number: data.invoice_number,
        issue_date: data.issue_date,
        due_date: data.due_date,
        expense_account_id: data.expense_account_id,
      })
      return res.data as { order: PurchaseOrder; invoice: SupplierInvoice }
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['purchases', 'orders'] })
      qc.invalidateQueries({ queryKey: ['purchases', 'invoices'] })
      qc.invalidateQueries({ queryKey: purchaseKeys.order(vars.id) })
    },
  })
}

export function useOrderApprovers() {
  return useQuery({
    queryKey: ['purchases', 'orders', 'approvers'],
    queryFn: async () => {
      const res = await apiClient.get('/purchases/orders/approvers/')
      return res.data as { approvers: ApproverUser[]; count: number }
    },
  })
}

export function useSendPurchaseOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/purchases/orders/${id}/send/`)
      return res.data as PurchaseOrder
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'orders'] })
    },
  })
}

export function useReceivePurchaseOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const res = await apiClient.post(`/purchases/orders/${id}/receive/`, { notes })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'orders'] })
      qc.invalidateQueries({ queryKey: ['purchases', 'receipts'] })
    },
  })
}

export function useCancelPurchaseOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const res = await apiClient.post(`/purchases/orders/${id}/cancel/`, { reason })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'orders'] })
    },
  })
}

// ─── COM-03: Recepciones (read-only) ─────────────────────────────────────────

export function useGoodsReceipts(params?: { supplier_id?: string; page?: number }) {
  return useQuery<GoodsReceiptListResponse>({
    queryKey: purchaseKeys.receipts(params),
    queryFn: async () => {
      const res = await apiClient.get('/purchases/receipts/', { params })
      return res.data
    },
  })
}

// ─── COM-04: Facturas de Proveedor ───────────────────────────────────────────

export function useTriggerRCVDownload() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (periodo?: string) => {
      const res = await apiClient.post('/purchases/rcv/download/', periodo ? { periodo } : {})
      return res.data
    },
    onSuccess: () => {
      // Invalidate invoices after a delay to let the task process
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['purchases', 'invoices'] })
        qc.invalidateQueries({ queryKey: ['purchases', 'dashboard'] })
      }, 5000)
    },
  })
}

export function useSupplierInvoices(params?: {
  status?: string
  supplier_id?: string
  search?: string
  accounting_status?: string
  payment_status?: string
  source?: string
  document_type_sii?: string
  amount_min?: string
  amount_max?: string
  date_from?: string
  date_to?: string
  due_from?: string
  due_to?: string
  rcv_fecha_recepcion_from?: string
  rcv_fecha_recepcion_to?: string
  orphan?: string
  ordering?: string
  page?: number
  page_size?: number
}) {
  return useQuery<SupplierInvoiceListResponse>({
    queryKey: purchaseKeys.invoices(params),
    queryFn: async () => {
      const res = await apiClient.get('/purchases/invoices/', { params })
      return res.data
    },
  })
}

export function useSupplierInvoice(id: string | null, opts?: { keepPrevious?: boolean }) {
  return useQuery<SupplierInvoice>({
    queryKey: purchaseKeys.invoice(id ?? ''),
    queryFn: async () => {
      const res = await apiClient.get(`/purchases/invoices/${id}/`)
      return res.data
    },
    enabled: !!id,
    // Opt-in, mismo criterio que `useSaleDocument`: al cambiar de factura desde
    // el rail de la Vista 360 conserva la anterior en pantalla mientras carga la
    // nueva. Sin esto `isLoading` vuelve a true, el componente se reemplaza por
    // un spinner de pantalla completa y se ve como si la pagina se recargara —
    // que es exactamente el sintoma reportado, y por lo que ventas no lo tiene.
    // NO usar en modales: el id cambia al reabrir y mostraria datos viejos.
    ...(opts?.keepPrevious ? { placeholderData: keepPreviousData } : {}),
  })
}

export function useCreateSupplierInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<SupplierInvoice>) => {
      const res = await apiClient.post('/purchases/invoices/', data)
      return res.data as SupplierInvoice
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'invoices'] })
    },
  })
}

export function useUpdateSupplierInvoice(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<SupplierInvoice>) => {
      const res = await apiClient.patch(`/purchases/invoices/${id}/`, data)
      return res.data as SupplierInvoice
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'invoices'] })
      qc.invalidateQueries({ queryKey: purchaseKeys.invoice(id) })
    },
  })
}

export function useUpdateInvoiceReferencias(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (referencias: APInvoiceReference[]) => {
      const res = await apiClient.patch(
        `/purchases/invoices/${id}/referencias/`,
        { referencias },
      )
      return res.data as SupplierInvoice
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'invoices'] })
      qc.invalidateQueries({ queryKey: purchaseKeys.invoice(id) })
    },
  })
}

/** Edita notas/observaciones de una factura en cualquier estado (no contable). */
export function useUpdateInvoiceNotes(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (notes: string) => {
      const res = await apiClient.patch(`/purchases/invoices/${id}/metadata/`, { notes })
      return res.data as SupplierInvoice
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'invoices'] })
      qc.invalidateQueries({ queryKey: purchaseKeys.invoice(id) })
    },
  })
}

export function useApproveSupplierInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, accountId }: { id: string; accountId?: string }) => {
      const body = accountId ? { account_id: accountId } : {}
      const res = await apiClient.post(`/purchases/invoices/${id}/approve/`, body)
      return res.data as SupplierInvoice
    },
    onSuccess: (data) => {
      avisarSiNoSeContabilizo(data)
      qc.invalidateQueries({ queryKey: ['purchases', 'invoices'] })
    },
  })
}

/**
 * Responder al SII una factura sin XML (#1637): acuse (ERM), aceptación (ACD) o
 * reclamo (RCD/RFP/RFT). Síncrono: si el SII lo rechaza, el error trae su motivo.
 */
export function useResponderSiiSinXml() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, accion, observaciones }: { id: string; accion: string; observaciones?: string }) => {
      const res = await apiClient.post(`/purchases/invoices/${id}/sii-response/`, { accion, observaciones })
      return res.data as SupplierInvoice
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'invoices'] })
    },
  })
}

/**
 * Anular / Eliminar una factura de proveedor.
 *
 * `reverseVoucher` genera el asiento de reversión del comprobante aprobado en el
 * mismo paso. Sin él, el backend responde 409 VOUCHER_APPROVED: un asiento firme
 * no se deja sin efecto sin decisión explícita del contador.
 */
type InvoiceCancelArgs = string | { id: string; reverseVoucher?: boolean }

const invoiceArgs = (a: InvoiceCancelArgs) =>
  typeof a === 'string' ? { id: a, reverseVoucher: false } : a

export function useCancelSupplierInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: InvoiceCancelArgs) => {
      const { id, reverseVoucher } = invoiceArgs(args)
      const res = await apiClient.post(
        `/purchases/invoices/${id}/cancel/`,
        reverseVoucher ? { reverse_voucher: true } : {},
      )
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'invoices'] })
      qc.invalidateQueries({ queryKey: ['accounting', 'vouchers'] })
    },
  })
}

export function useDeleteSupplierInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: InvoiceCancelArgs) => {
      const { id, reverseVoucher } = invoiceArgs(args)
      await apiClient.delete(
        `/purchases/invoices/${id}/delete/${reverseVoucher ? '?reverse_voucher=true' : ''}`,
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'invoices'] })
      qc.invalidateQueries({ queryKey: ['accounting', 'vouchers'] })
    },
  })
}

/** #71: solicita al proveedor reenviar el XML del DTE a la casilla de intercambio. */
export function useRequestSupplierXml(id: string) {
  return useMutation({
    mutationFn: async (email: string) => {
      const res = await apiClient.post(`/purchases/invoices/${id}/request-xml/`, { email })
      return res.data as { sent_to: string; intercambio: string }
    },
  })
}

// ─── COM-05: Pagos a Proveedores ─────────────────────────────────────────────

export function useSupplierPayments(params?: {
  status?: string
  supplier_id?: string
  invoice_id?: string
  accounting_status?: string
  search?: string
  date_from?: string
  date_to?: string
  ordering?: string
  page?: number
  page_size?: number
}) {
  return useQuery<SupplierPaymentListResponse>({
    queryKey: purchaseKeys.payments(params),
    queryFn: async () => {
      const res = await apiClient.get('/purchases/payments/', { params })
      return res.data
    },
  })
}

export function useSupplierPayment(id: string) {
  return useQuery<SupplierPayment>({
    queryKey: purchaseKeys.payment(id),
    queryFn: async () => {
      const res = await apiClient.get(`/purchases/payments/${id}/`)
      return res.data
    },
    enabled: !!id,
  })
}

export function useCreateSupplierPayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<SupplierPayment>) => {
      const res = await apiClient.post('/purchases/payments/', data)
      return res.data as SupplierPayment
    },
    onSuccess: (data) => {
      avisarSiNoSeContabilizo(data)
      qc.invalidateQueries({ queryKey: ['purchases', 'payments'] })
      qc.invalidateQueries({ queryKey: ['purchases', 'payables'] })
    },
  })
}

export function useUpdateSupplierPayment(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<SupplierPayment>) => {
      const res = await apiClient.patch(`/purchases/payments/${id}/`, data)
      return res.data as SupplierPayment
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'payments'] })
      qc.invalidateQueries({ queryKey: purchaseKeys.payment(id) })
    },
  })
}

export function useConfirmSupplierPayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/purchases/payments/${id}/confirm/`)
      return res.data as SupplierPayment
    },
    onSuccess: (data) => {
      avisarSiNoSeContabilizo(data)
      qc.invalidateQueries({ queryKey: ['purchases', 'payments'] })
      qc.invalidateQueries({ queryKey: ['purchases', 'invoices'] })
      qc.invalidateQueries({ queryKey: ['purchases', 'payables'] })
    },
  })
}

export function useCancelSupplierPayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/purchases/payments/${id}/cancel/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'payments'] })
      qc.invalidateQueries({ queryKey: ['purchases', 'payables'] })
    },
  })
}

// ─── COM-AP: Facturas por pagar ───────────────────────────────────────────────

export type PayableInvoiceStatus = 'pending' | 'partial' | 'paid' | 'overdue' | 'paid_historical'

export interface PayableInvoice {
  id: string
  supplier: string
  supplier_name: string
  supplier_tax_id: string
  invoice_number: string
  issue_date: string | null
  due_date: string | null
  total: string
  paid_amount: string
  outstanding: string
  /** Estado CALCULADO (#1486), con la misma regla que las pestañas: con saldo y
   *  vencida → 'overdue' aunque el guardado siga en 'pending'. */
  payment_status: PayableInvoiceStatus
  /** Vencida con abonos: sale en «Pago Parcial» y en «Vencido»; la pastilla
   *  dice «Vencido · parcial». */
  is_partial?: boolean
  status: string
  /** Total tributario 0 pero con monto no facturable: el outstanding es el monto
   *  real (abs). Al pagarlo, el comprobante nace en borrador para revisar la cuenta. */
  is_non_billable?: boolean
  // Moneda original (facturas intl). null si la factura es CLP.
  currency_original?: string | null
  total_original?: string | null
  paid_original?: string | null
  outstanding_original?: string | null
}

export interface PayableInvoicesResponse {
  invoices: PayableInvoice[]
  count: number
  page: number
  page_size: number
  pages: number
}

export function usePayableInvoices(params?: {
  payment_status?: string
  supplier_id?: string
  search?: string
  date_from?: string
  date_to?: string
  aging_bucket?: string
  ordering?: string
  page?: number
  page_size?: number
}) {
  return useQuery<PayableInvoicesResponse>({
    queryKey: ['purchases', 'payables', params],
    queryFn: async () => {
      const res = await apiClient.get('/purchases/payables/', { params })
      return res.data as PayableInvoicesResponse
    },
  })
}

export interface ApAgingBucket {
  count: number
  total: string
}

export interface ApAgingSummaryResponse {
  buckets: {
    current: ApAgingBucket
    '1_30': ApAgingBucket
    '31_60': ApAgingBucket
    '61_90': ApAgingBucket
    over_90: ApAgingBucket
  }
  total_outstanding: string
}

// Resumen de antigüedad de saldos por pagar (tramos navegables, como AR aging).
export function useApAgingSummary() {
  return useQuery<ApAgingSummaryResponse>({
    queryKey: ['purchases', 'payables', 'aging-summary'],
    queryFn: async () => {
      const res = await apiClient.get('/purchases/payables/aging-summary/')
      return res.data as ApAgingSummaryResponse
    },
    staleTime: 60 * 1000,
  })
}

// ─── CSV Import ───────────────────────────────────────────────────────────────

export interface CSVImportResult {
  created: number
  updated: number
  errors: { row: number; error: string | Record<string, string[]> }[]
  total_rows: number
}

export function useImportSuppliersCSV() {
  const qc = useQueryClient()
  return useMutation<CSVImportResult, Error, { file: File; mapping: Record<string, string> }>({
    mutationFn: async ({ file, mapping }) => {
      const form = new FormData()
      form.append('file', file)
      form.append('mapping', JSON.stringify(mapping))
      const res = await apiClient.post('/purchases/suppliers/import-csv/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // ⚠️ El default de 30 s es correcto para un listado y absurdo para importar
        // cientos de filas: una importación real de 745 clientes tardó 35 s y el
        // navegador mostró «timeout» sobre un archivo que SÍ se había importado
        // completo — el peor error posible, porque invita a reintentar.
        timeout: IMPORT_TIMEOUT_MS,
      })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases', 'suppliers'] })
    },
  })
}
